import type { MutableRefObject } from 'react';
import type { Crepe } from '@milkdown/crepe';
import { collab } from '@milkdown/plugin-collab';
import { nord } from '@milkdown/theme-nord';
import type { usePluginViewFactory, useNodeViewFactory } from '@prosemirror-adapter/react';

import { EmojiTooltipView, emojiCorePlugins, emojiSuggestProsePlugin, emojiTooltip } from '../../plugins/emoji';
import { taskListOrphanPastePlugin } from '../../plugins/taskListOrphanPastePlugin';
import { vscodeMarkdownPastePlugin } from '../../plugins/vscodeMarkdownPastePlugin';
import { smartTextPlugin } from '../../plugins/smartText';
import { getPlatformPersistPlugins } from '../../plugins/hocuspocus-platform-schema';
import { noticePlugin, configureNoticeNodeView, noticeStyleTooltip, NoticeTooltipView, noticeKeymap } from '../../nodes/notice-block';
import { variableContentPlugin, configureVariableContentNodeViews, variableContentKeymap } from '../../nodes/variable-content';
import { linkTooltip, linkTooltipConfig, LinkTooltipView, tentativeLinkPlugin } from '../../plugins/link-tooltip';
import { configureGrammarCheck } from '../../plugins/grammarCheck';
import { configureEmbeds } from '../../nodes/embed';
import { configureVideo } from '../../nodes/video';
import { configureAttachment } from '../../nodes/attachment';
import { configureUpload } from '../../plugins/uploadPlugin';
import { headingIdPlugin } from '../../plugins/headingId';
import { formattingToolbarTableCellPlugin } from '../../plugins/formattingToolbarTableCellPlugin';
import { platformImageBlockPlugins } from '../../plugins/platform-image-block';
import { tableCellImagePastePlugin, tableCellImageRemark, tableCellImageTransform } from '../../plugins/tableCellImagePlugin';
import { imageErrorFallbackMilkdownPlugin } from '../../plugins/imageErrorFallbackPlugin';
import { tableHardBreakPlugin } from '../../plugins/tableHardBreakPlugin';
import { documentIssuesDecorationPlugin } from '../../plugins/documentIssuesDecoration';
import { reviewSuggestionsDecorationPlugin } from '../../plugins/reviewSuggestions';
import { configureHighlight } from '../../plugins/textHighlight';
import { configureEmphasis } from '../../plugins/emphasis/configure';
import { unknownDirectiveFallbackPlugin } from '../../plugins/remarkUnknownDirectiveFallback';
import { legacyHardBreakPlugin } from '../../plugins/remarkLegacyHardBreak';
import { gluedStarEmphasisPlugin } from '../../plugins/remarkGluedStarEmphasis';
import fallbackImageSrc from '../../assets/image-error.svg';
import type {
  BrainfishEditorProps,
  DocumentIssue,
  MediaUploadHandler,
  ReviewSuggestionResolveOutcome,
  ReviewSuggestionsResolveSummary,
} from '../../types';

interface RegisterPluginsOpts {
  pluginViewFactory: ReturnType<typeof usePluginViewFactory>;
  nodeViewFactory: ReturnType<typeof useNodeViewFactory>;
  readOnly: boolean;
  hasCollab: boolean;
  onSearchLink?: BrainfishEditorProps['onSearchLink'];
  onUploadImage?: MediaUploadHandler;
  onUploadVideo?: MediaUploadHandler;
  onUploadFile?: MediaUploadHandler;
  issues?: DocumentIssue[];
  onReviewSuggestionSelectRef: MutableRefObject<((suggestionId: string | null) => void) | undefined>;
  onReviewSuggestionResolveRef: MutableRefObject<((suggestionId: string, outcome: ReviewSuggestionResolveOutcome) => void) | undefined>;
  onReviewSuggestionsResolvedRef: MutableRefObject<((summary: ReviewSuggestionsResolveSummary) => void) | undefined>;
  imageErrorFallbackUrlRef: MutableRefObject<string | undefined>;
}

/**
 * Registers all custom Milkdown plugins on the Crepe editor.
 *
 * **Order matters.** ProseMirror iterates plugins in reverse for `handleKeyDown`,
 * and Crepe's clipboard plugin uses `handlePaste` (not `transformPasted`), so
 * paste-rewriting plugins must be prepended. Notable orderings:
 * - `emojiSuggestProsePlugin` registers second-to-last so its keydown runs first.
 * - `tableHardBreakPlugin` registers last (after emoji) so this runs first on keydown.
 */
export function registerPlugins(crepe: Crepe, opts: RegisterPluginsOpts) {
  const {
    pluginViewFactory,
    nodeViewFactory,
    readOnly,
    hasCollab,
    onSearchLink,
    onUploadImage,
    onUploadVideo,
    onUploadFile,
    issues,
    onReviewSuggestionSelectRef,
    onReviewSuggestionResolveRef,
    onReviewSuggestionsResolvedRef,
    imageErrorFallbackUrlRef,
  } = opts;

  // Orphan `[ ]` / `[x]` lines → GFM task list on paste (prepends before Crepe's bundled clipboard plugin)
  crepe.editor.use(taskListOrphanPastePlugin);
  // Canonical italic serialize `_text_` (schema default + stringify). Must run at create() before mark schema.
  configureEmphasis(crepe);
  // VS Code / Cursor copies include `vscode-editor-data`; Milkdown would paste that as a code fence — parse md instead.
  crepe.editor.use(vscodeMarkdownPastePlugin);
  // Crepe clipboard uses `handlePaste` (not `transformPasted`); prepend so cell + image-block pastes rewrite.
  crepe.editor.use(tableCellImagePastePlugin);

  // Platform image-block dimensions: remark parse, schema extension, runtime width/height sync.
  // Must register after Crepe's imageBlockComponent and before tableCellImageRemark.
  platformImageBlockPlugins.forEach((plugin) => crepe.editor.use(plugin));

  // GFM cells only allow paragraphs. `remark-image-block` (Crepe) lifts lone `![](url)` to `image-block` everywhere,
  // which is invalid inside `tableCell` — this remark pass runs after that and restores `paragraph` + mdast `image`.
  crepe.editor.use(tableCellImageRemark);
  // Cut/paste of an `image-block` into a cell: rewrite slice to inline `image` before insert.
  crepe.editor.use(tableCellImageTransform);
  // Inline `image` nodes (incl. table cells) have no `onError`; mirror ImageBlock fallback via capture.
  crepe.editor.use(imageErrorFallbackMilkdownPlugin(() => imageErrorFallbackUrlRef.current || fallbackImageSrc));

  crepe.editor.use(emojiCorePlugins);
  crepe.editor.config((ctx) => {
    ctx.set(emojiTooltip.key, {
      view: pluginViewFactory({ component: EmojiTooltipView }),
    });
  });
  crepe.editor.use(emojiTooltip);

  /**
   * NOTICE CALLOUT BLOCKS CONFIGURATION (:::info, :::warning, :::tip)
   */
  noticePlugin.forEach((plugin) => crepe.editor.use(plugin));
  // Notice NodeView (React component via prosemirror-adapter)
  crepe.editor.config((ctx) => configureNoticeNodeView(ctx, nodeViewFactory));
  // Notice style tooltip (React component via prosemirror-adapter)
  crepe.editor.config((ctx) => {
    ctx.set(noticeStyleTooltip.key, {
      view: pluginViewFactory({ component: NoticeTooltipView }),
    });
  });
  crepe.editor.use(noticeStyleTooltip);
  // Notice Tab keymap (Tab from notice block focuses tooltip)
  crepe.editor.use(noticeKeymap);

  /**
   * VARIABLE CONTENT BLOCKS (:::::variants / ::::variant)
   * Reuses remark-directive from noticePlugin — do not register a second instance.
   * Channels for UI come from VariableContentChannelsContext (EditorCore), not a Milkdown slice.
   */
  variableContentPlugin.forEach((plugin) => crepe.editor.use(plugin));
  crepe.editor.config((ctx) => configureVariableContentNodeViews(ctx, nodeViewFactory));
  // Tab from variant body focuses channel controls (return trip handled in NodeView)
  crepe.editor.use(variableContentKeymap);

  // Upload plugin (drag-and-drop, paste for images, videos, and file attachments)
  configureUpload(crepe, { onUploadImage, onUploadVideo, onUploadFile });

  /**
   * EMBED BLOCKS CONFIGURATION (YouTube, iframes, etc.)
   * Handles: YouTube URLs, ::iframe{src="..."} input rule, and rendering
   */
  configureEmbeds(crepe, { nodeViewFactory, pluginViewFactory });

  // Unknown `:word` / `::word` from remark-directive crash Milkdown unless normalized to plain text.
  unknownDirectiveFallbackPlugin.forEach((plugin) => crepe.editor.use(plugin));
  // Legacy stored markdown uses literal ` \n ` for hard breaks (rich-markdown-editor format).
  legacyHardBreakPlugin.forEach((plugin) => crepe.editor.use(plugin));
  // `**bold***.*` leftover stars after CommonMark flanking (Platform serializer glue).
  gluedStarEmphasisPlugin.forEach((plugin) => crepe.editor.use(plugin));

  /**
   * VIDEO BLOCKS CONFIGURATION (native video files)
   * Handles: Video file URLs, /video slash command, and rendering
   */
  configureVideo(crepe, { nodeViewFactory, onUploadVideo });

  /**
   * ATTACHMENT BLOCKS (Platform PDF/file cards from attachments.redirect links)
   */
  configureAttachment(crepe, { nodeViewFactory, onUploadFile });

  /**
   * TEXT HIGHLIGHT (==highlight== syntax, <mark> in HTML)
   */
  configureHighlight(crepe);

  /**
   * LINK TOOLTIP CONFIGURATION (custom link editing with search)
   */
  // Set link tooltip config with search callbacks
  crepe.editor.config((ctx) => {
    ctx.set(linkTooltipConfig.key, {
      onSearchLink,
    });
  });
  // Link tooltip view (React component via prosemirror-adapter)
  crepe.editor.config((ctx) => {
    ctx.set(linkTooltip.key, {
      view: pluginViewFactory({ component: LinkTooltipView }),
    });
  });
  crepe.editor.use(linkTooltipConfig);
  crepe.editor.use(linkTooltip);
  // Tentative link cleanup plugin (removes abandoned empty links)
  crepe.editor.use(tentativeLinkPlugin);

  // Document issues decoration (health check: broken links, images, inappropriate, grammatical)
  crepe.editor.use(documentIssuesDecorationPlugin(issues));
  crepe.editor.use(
    reviewSuggestionsDecorationPlugin(onReviewSuggestionSelectRef, onReviewSuggestionResolveRef, onReviewSuggestionsResolvedRef),
  );

  // Grammar check (loading decoration for pulsing highlight during API call)
  configureGrammarCheck(crepe);

  // Use Milkdown's built-in collab plugin when collaboration is enabled
  if (hasCollab) {
    crepe.editor.use(collab);
  }

  crepe.setReadonly(readOnly);
  crepe.editor.config(nord);
  smartTextPlugin().forEach((plugin) => crepe.editor.use(plugin));
  getPlatformPersistPlugins({ enableNormalizer: hasCollab }).forEach((plugin) => crepe.editor.use(plugin));

  // Heading ID plugin - adds id attributes to headings for TOC anchor navigation
  crepe.editor.use(formattingToolbarTableCellPlugin);
  crepe.editor.use(headingIdPlugin);

  // Emoji autocomplete: register last so `handleKeyDown` runs before other plugins (PM iterates plugins in reverse).
  crepe.editor.use(emojiSuggestProsePlugin);

  // Shift+Enter → hard break inside table cells (after emoji so this runs first on keydown)
  crepe.editor.use(tableHardBreakPlugin);
}
